import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../stores/store';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import { useSnackbar } from 'notistack';
import { useSetMenu } from '../../../hooks/useSetMenu';
import LoadingOverlay from '../../Generic/LoadingOverlay';
import { Pie, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const EvaluationReport = () => {
  const { templateId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mainStore, evaluationStore } = useStore();
  const axiosPrivate = useAxiosPrivate();
  const { enqueueSnackbar } = useSnackbar();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [evaluationData, setEvaluationData] = useState(null);
  const [templateInfo, setTemplateInfo] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [barChartData, setBarChartData] = useState(null);
  const [selectedExportFormat, setSelectedExportFormat] = useState('csv');
  const [exportSection, setExportSection] = useState('responses');
  const [exportInProgress, setExportInProgress] = useState(false);
  
useSetMenu(mainStore.EvaluationMenuType, mainStore.EvaluationMenuNo);
  
  useEffect(() => {
    const fetchEvaluationData = async () => {
      setLoading(true);
      try {
        const templateResponse = await axiosPrivate.get(`/EvaluationTemplates/${templateId}`);
        setTemplateInfo(templateResponse.data);
        
        const instancesResponse = await axiosPrivate.get(`/EvaluationInstances/GetEvaluationInstancesByTemplate/${templateId}`);
        const instances = instancesResponse.data;
        
        const stats = {
          totalInstances: instances.length,
          submittedInstances: instances.filter(i => i.instanceStatusName === "Submitted").length,
          pendingInstances: instances.filter(i => i.instanceStatusName === "Pending").length
        };
        
        const data = {
          template: templateResponse.data,
          instances: instances,
          stats: stats
        };
        
        setEvaluationData(data);
        
        evaluationStore.setEvaluationStats(stats);
        
        prepareChartData(data);
      } catch (err) {
        setError(err.response?.data?.message || t('Error fetching evaluation data'));
        enqueueSnackbar(t('errorLoadingEvaluation'), { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };
    
    fetchEvaluationData();
    
    return () => {
      evaluationStore.clearEvaluationStats();
    };
  }, [axiosPrivate, templateId, t, enqueueSnackbar, evaluationStore]);
  
  const prepareChartData = (data) => {
    const pieData = {
      labels: [t('submitted'), t('pending')],
      datasets: [
        {
          data: [data.stats.submittedInstances, data.stats.pendingInstances],
          backgroundColor: ['rgba(75, 192, 192, 0.7)', 'rgba(255, 206, 86, 0.7)'],
          borderColor: ['rgba(75, 192, 192, 1)', 'rgba(255, 206, 86, 1)'],
          borderWidth: 1,
        },
      ],
    };
    setChartData(pieData);
    
    if (data.stats.submittedInstances > 0) {
      const barData = {
        labels: ['Question 1', 'Question 2', 'Question 3', 'Question 4'],
        datasets: [
          {
            label: t('averageScore'),
            data: [4.2, 3.8, 4.5, 3.2],
            backgroundColor: 'rgba(54, 162, 235, 0.7)',
            borderColor: 'rgba(54, 162, 235, 1)',
            borderWidth: 1,
          },
        ],
      };
      setBarChartData(barData);
    }
  };
  
  const exportData = () => {
    if (!evaluationData) {
      enqueueSnackbar(t('noDataToExport'), { variant: 'warning' });
      return;
    }
    
    setExportInProgress(true);
    
    try {
      if (exportSection === 'responses') {
        exportResponses();
      } else if (exportSection === 'statistics') {
        exportStatistics();
      }
    } catch (error) {
      console.error('Export error:', error);
      enqueueSnackbar(t('errorExporting'), { variant: 'error' });
    } finally {
      setExportInProgress(false);
    }
  };
  
  const exportResponses = () => {
    const { instances, template } = evaluationData;
    const submittedInstances = instances.filter(i => i.instanceStatusName === "Submitted");
    
    if (submittedInstances.length === 0) {
      enqueueSnackbar(t('noSubmittedResponses'), { variant: 'info' });
      return;
    }
    
    if (selectedExportFormat === 'csv') {
      let csvContent = "StudentName,SubmittedDate";
      
      if (submittedInstances.length > 0 && submittedInstances[0].answers) {
        submittedInstances[0].answers.forEach((answer, index) => {
          csvContent += `,Question${index + 1}`;
        });
      }
      csvContent += "\n";
      
      submittedInstances.forEach(instance => {
        const row = [
          instance.studentName || 'Unknown',
          instance.submittedDate ? new Date(instance.submittedDate).toLocaleString() : 'N/A'
        ];
        
        if (instance.answers) {
          instance.answers.forEach(answer => {
            let answerText = '';
            if (answer.freeText && answer.freeText.trim() !== '') {
              answerText = answer.freeText.replace(/"/g, '""');
            } else if (answer.selectedOptions && answer.selectedOptions.length > 0) {
              answerText = answer.selectedOptions.map(o => `${o.optionCode} - ${o.optionLabel}`).join('; ');
            }
            row.push(`"${answerText}"`);
          });
        }
        
        csvContent += row.join(',') + "\n";
      });
      
      downloadCSV(csvContent, `${template.nameLNG1}_responses.csv`);
    }
  };
  
  const exportStatistics = () => {
    const { stats, template } = evaluationData;
    
    if (selectedExportFormat === 'csv') {
      let csvContent = "Metric,Value\n";
      csvContent += `"${t('totalInstances')}",${stats.totalInstances}\n`;
      csvContent += `"${t('submittedInstances')}",${stats.submittedInstances}\n`;
      csvContent += `"${t('pendingInstances')}",${stats.pendingInstances}\n`;
      csvContent += `"${t('completionRate')}",${stats.totalInstances > 0 ? ((stats.submittedInstances / stats.totalInstances) * 100).toFixed(2) : 0}%\n`;
      
      
      downloadCSV(csvContent, `${template.nameLNG1}_statistics.csv`);
    }
  };
  
  const downloadCSV = (content, filename) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  if (loading) {
    return <LoadingOverlay />;
  }
  
  if (error) {
    return (
      <div className="container py-4">
        <div className="card rounded-0 borderLine">
          <div className="card-header pb-0 rounded-0 bg-danger cardHeaderBackground">
            <p className="crudTitle">{t('evaluationReports')}</p>
          </div>
          <div className="card-body p-3">
            <div className="alert alert-danger">{error}</div>
            <button 
              className="btn btn-secondary"
              onClick={() => navigate('/EvaluationTemplate/List')}
            >
              {t('backToList')}
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  if (!evaluationData) {
    return (
      <div className="container py-4">
        <div className="card rounded-0 borderLine">
          <div className="card-header pb-0 rounded-0 bg-danger cardHeaderBackground">
            <p className="crudTitle">{t('evaluationReports')}</p>
          </div>
          <div className="card-body p-3">
            <div className="alert alert-warning">{t('noEvaluationData')}</div>
            <button 
              className="btn btn-secondary"
              onClick={() => navigate('/EvaluationTemplate/List')}
            >
              {t('backToList')}
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container py-4">
      {}
      <div className="card rounded-0 borderLine mb-4">
        <div className="card-header pb-0 rounded-0 bg-danger cardHeaderBackground">
          <p className="crudTitle">
            {t('evaluationReport')}: {evaluationData.template.nameLNG1}
          </p>
        </div>
        <div className="card-body p-3">
          <div className="row">
            <div className="col-md-6">
              <p><strong>{t('description')}:</strong> {evaluationData.template.descriptionLNG1}</p>
              <p><strong>{t('forStaff')}:</strong> {evaluationData.template.forStaff ? t('yes') : t('no')}</p>
            </div>
            <div className="col-md-6">
              <p><strong>{t('startDate')}:</strong> {evaluationData.template.startDate ? new Date(evaluationData.template.startDate).toLocaleDateString() : '-'}</p>
              <p><strong>{t('endDate')}:</strong> {evaluationData.template.endDate ? new Date(evaluationData.template.endDate).toLocaleDateString() : '-'}</p>
            </div>
          </div>
        </div>
      </div>
      
      {}
      <div className="card rounded-0 borderLine mb-4">
        <div className="card-header pb-0 rounded-0 bg-info cardHeaderBackground">
          <p className="crudTitle">{t('statisticsOverview')}</p>
        </div>
        <div className="card-body p-3">
          <div className="row">
            <div className="col-md-4">
              <div className="border rounded p-3 text-center mb-3">
                <h3>{evaluationData.stats.totalInstances}</h3>
                <p className="text-muted">{t('totalInstances')}</p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="border rounded p-3 text-center mb-3 bg-success bg-opacity-25">
                <h3>{evaluationData.stats.submittedInstances}</h3>
                <p className="text-muted">{t('submittedInstances')}</p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="border rounded p-3 text-center mb-3 bg-warning bg-opacity-25">
                <h3>{evaluationData.stats.pendingInstances}</h3>
                <p className="text-muted">{t('pendingInstances')}</p>
              </div>
            </div>
          </div>
          
          <div className="row mt-4">
            <div className="col-md-6">
              <h5 className="mb-3">{t('completionRate')}</h5>
              {chartData && (
                <div className="chart-container" style={{ height: '300px' }}>
                  <Pie data={chartData} options={{ maintainAspectRatio: false }} />
                </div>
              )}
            </div>
            
            <div className="col-md-6">
              <h5 className="mb-3">{t('questionResponses')}</h5>
              {barChartData ? (
                <div className="chart-container" style={{ height: '300px' }}>
                  <Bar 
                    data={barChartData} 
                    options={{ 
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          max: 5
                        }
                      } 
                    }} 
                  />
                </div>
              ) : (
                <div className="alert alert-info">
                  {t('notEnoughDataForVisualization')}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {}
      <div className="card rounded-0 borderLine mb-4">
        <div className="card-header pb-0 rounded-0 bg-success cardHeaderBackground">
          <p className="crudTitle">{t('exportTools')}</p>
        </div>
        <div className="card-body p-3">
          <div className="row">
            <div className="col-md-4">
              <div className="form-group mb-3">
                <label className="form-label">{t('exportFormat')}</label>
                <select 
                  className="form-select"
                  value={selectedExportFormat}
                  onChange={(e) => setSelectedExportFormat(e.target.value)}
                >
                  <option value="csv">CSV</option>
                  <option value="excel" disabled>Excel (Coming Soon)</option>
                  <option value="pdf" disabled>PDF (Coming Soon)</option>
                </select>
              </div>
            </div>
            
            <div className="col-md-4">
              <div className="form-group mb-3">
                <label className="form-label">{t('exportSection')}</label>
                <select 
                  className="form-select"
                  value={exportSection}
                  onChange={(e) => setExportSection(e.target.value)}
                >
                  <option value="responses">{t('individualResponses')}</option>
                  <option value="statistics">{t('statistics')}</option>
                </select>
              </div>
            </div>
            
            <div className="col-md-4 d-flex align-items-end">
              <button 
                className="btn btn-success w-100 mb-3"
                onClick={exportData}
                disabled={exportInProgress}
              >
                {exportInProgress ? (
                  <span>
                    <i className="bi bi-hourglass-split me-2"></i>
                    {t('exporting')}
                  </span>
                ) : (
                  <span>
                    <i className="bi bi-file-earmark-arrow-down me-2"></i>
                    {t('export')}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {}
      <div className="card rounded-0 borderLine mb-4">
        <div className="card-header pb-0 rounded-0 bg-primary cardHeaderBackground">
          <p className="crudTitle">{t('evaluationInstances')}</p>
        </div>
        <div className="card-body p-3">
          {evaluationData.instances.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-bordered table-hover">
                <thead className="bg-light">
                  <tr>
                    <th>{t('student')}</th>
                    <th>{t('course')}</th>
                    <th>{t('status')}</th>
                    <th>{t('submittedDate')}</th>
                    <th>{t('actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {evaluationData.instances.map(instance => (
                    <tr key={instance.id}>
                      <td>{instance.studentName || '-'}</td>
                      <td>{instance.subjectName || '-'}</td>
                      <td>
                        <span className={`badge ${
                          instance.instanceStatusName === 'Pending' ? 'bg-warning' : 
                          instance.instanceStatusName === 'Submitted' ? 'bg-success' : 
                          'bg-secondary'
                        }`}>
                          {instance.instanceStatusName}
                        </span>
                      </td>
                      <td>
                        {instance.submittedDate 
                          ? new Date(instance.submittedDate).toLocaleString() 
                          : '-'
                        }
                      </td>
                      <td>
                        <button 
                          className="btn btn-sm btn-primary me-2"
                          onClick={() => navigate(`/Evaluation/View/${instance.id}`)}
                        >
                          <i className="bi bi-eye me-1"></i>
                          {t('view')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="alert alert-info">{t('noEvaluationInstances')}</div>
          )}
        </div>
      </div>
      
      <div className="d-flex justify-content-end mt-4">
        <button 
          className="btn btn-secondary"
          onClick={() => navigate('/EvaluationTemplate/List')}
        >
          {t('backToList')}
        </button>
      </div>
    </div>
  );
};

export default EvaluationReport; 